import {
    Component,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    computed,
    input,
} from '@angular/core';
import { PCP_LOGGER } from '@pcp/logging';
import { DomMutation } from 'shared/dom-mutation-core';
import {
    CanvasInteractionEvent,
    FavoriteImageSet,
    guid,
    Hotspot,
    IDisplaySlide,
    SlideType,
    SlideTypeCategory,
} from '@pcp/core/interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isDynamicallyCreatedSlideType, isImageSlideType } from '@pcp/core/util';
import {
    ConsumingPlayerDirective,
    isMutateInteractiveElementCommand,
    isMutateRubberElementCommand,
    isVideoCommand,
    PlaybackPositionChangedEvent,
    SlideCommand,
    UpdateSlideContentCommand,
    VideoCommand,
    VideoPlayerComponent,
    VideoPlayerComponentEvent,
} from "@pcp/streaming";
import {
    filter,
    map,
    Observable,
    Subject,
    Subscription,
    switchMap,
} from 'rxjs';
import { buildFavoriteImageSet } from './favorite-image-set';
import { takeUntil } from 'rxjs/operators';
import { UserClickedOnAddToFavoritesButtonEvent } from 'shared/classroom/data-access';
import {
    FabricSlideCanvasComponent,
    PerceptionSlideComponent,
    RubberElementComponent,
    HtmlElementBoardComponent,
    DifficultQuestionElementComponent,
    VotingElementComponent,
    VdoElementComponent,
    type RubberElementInput,
} from 'shared/classroom/preview-screen/slide-rendering';
import { DrawingToolName } from 'shared/classroom/toolbar/core';
import { NgStyle, AsyncPipe } from '@angular/common';
import { AddToFavoritesButtonComponent } from '../../components/add-to-favorites-button/add-to-favorites-button.component';
import { AsVideoElementInputPipe } from 'shared/classroom/preview-screen/ui-input-pipes';

/**
 * Wrapper component which shows exactly one slide of any type.
 * Delegates all input/output events to/from specialized components for the current slide type.
 *
 * __This component should be kept clean and should not depend on any services__
 */
@Component({
    selector: 'app-slide-preview-element',
    templateUrl: './slide-preview-element.component.html',
    styleUrls: ['./slide-preview-element.component.scss'],
    imports: [
        VideoPlayerComponent,
        ConsumingPlayerDirective,
        HtmlElementBoardComponent,
        RubberElementComponent,
        DifficultQuestionElementComponent,
        VotingElementComponent,
        VdoElementComponent,
        AddToFavoritesButtonComponent,
        NgStyle,
        AsyncPipe,
        AsVideoElementInputPipe,
        FabricSlideCanvasComponent,
        PerceptionSlideComponent,
    ],
})
export class SlidePreviewElementComponent implements OnDestroy, OnChanges {
    private readonly log = inject(PCP_LOGGER).createScoped('slide-sync');
    private readonly snackBar = inject(MatSnackBar);
    @Input() currentSlide: IDisplaySlide;
    @Input() slideTypeCategory: SlideTypeCategory = 'none';
    @Input() isFavorite = false;
    @Input() isOpenedFromHotspot = false;

    /** If the whole component should be hidden */
    @Input() @HostBinding('class.hidden') hidden = true;

    /** Enable/disable "add to favorites" button */
    @Input() favoritesDisabled = false;

    /** Input event emits whenever the content of the current slide should be reset */
    @Input() resetDynamicContent$: Observable<{ resetImageToBasic: boolean }>;

    @Input() svgTextboardPageNumber: number;

    /**
     * The preview element can have different view for presentation screens and non-presentation screens,
     * e.g. perception images are animated on the beamer but not on the teacher screen.
     *
     * Presentation screens are all screens which can be viewed by students (beamer, student and live(?)).
     */
    @Input() isPresentationScreen: boolean;

    /**
     * If true, the shown element cannot be controlled by the user.
     */
    @Input() disableElementUsage: boolean;

    /** If false, HTML element modifications/interactions will _not_ be emitted as Output */
    @Input() emitHtmlElementModifications = false;

    /**
     * Input if the current lesson is in paused state or not.
     * Some nested components react on pause state changes,
     * e.g. a VideoPlayer automatically pauses the video when the lesson gets paused.
     */
    @Input() isLessonPaused = false;

    @Input() enetToken?: string;

    @Input() loginCode?: string;
    @Input() presentationId?: guid;

    updateInteractiveElementCommand?: Observable<UpdateSlideContentCommand>;
    updateRubberElementCommand: Observable<CanvasInteractionEvent>;
    rubberInput: RubberElementInput;

    /** Any kind of video updates, e.g. pause, resume, seek etc. */
    updateVideoInputCommand?: Observable<VideoCommand>;

    /** Scale factor for the slide preview, received from parent component */
    readonly svgScale = input<number | null>(null);
    readonly isScaleReady = computed(() => this.svgScale() !== null);

    @Input() set commandStream$(commandStream$: undefined | Observable<SlideCommand>) {
        if (commandStream$) {
            this.updateVideoInputCommand = commandStream$.pipe(filter(isVideoCommand));
            this.updateInteractiveElementCommand = commandStream$.pipe(filter(isMutateInteractiveElementCommand));
            this.updateRubberElementCommand = commandStream$.pipe(
                filter(isMutateRubberElementCommand),
                map(
                    (updateCanvasCmd): CanvasInteractionEvent => ({
                        drag: updateCanvasCmd.changes.action === 'drawPoint',
                        mousePosition: {
                            x: updateCanvasCmd.changes.x,
                            y: updateCanvasCmd.changes.y,
                        },
                        touch: false,
                    }),
                ),
            );
        }
    }

    @Output() perceptionImageRevealed = new EventEmitter<void>();

    /** Hotspot inside an image element has been clicked by the user.*/
    @Output() hotspotClick = new EventEmitter<Hotspot>();

    /** HTML-Element DOM mutated/modified */
    @Output() domModified = new EventEmitter<DomMutation[]>();

    /** Interaction on rubber element */
    @Output() rubberElementInteraction = new EventEmitter<CanvasInteractionEvent>();

    /**
     * User has clicked on "add to favorites" for the current slide.
     * Emits transformed representation of the current slide to be saved.
     */
    // @Output() addToFavorites = new EventEmitter<SaveFavorite>();
    @Output() addToFavoritesClicked = new EventEmitter<UserClickedOnAddToFavoritesButtonEvent>();

    @Input() showPrevBookpagePageLink = false;
    @Input() showNextBookpagePageLink = false;

    @Input() suffix: string;
    @Input() isPresentationStarted: boolean = false;

    /**
     * Emitted when the user ist showing a bookpage and has clicked on "previous bookpage".
     * Adds the current bookpage as argument.
     * TODO: is the current slide needed as an argument?
     */
    @Output() prevBookpageClicked = new EventEmitter<IDisplaySlide>();

    /**
     * Emitted when the user ist showing a bookpage and has clicked on "next bookpage".
     * Adds the current bookpage as argument.
     */
    @Output() nextBookpageClicked = new EventEmitter<IDisplaySlide>();

    @Output() svgTextboardPageNumberChanged = new EventEmitter<number>();

    @Output() videoStartedPlaying = new EventEmitter<PlaybackPositionChangedEvent>();
    @Output() videoPaused = new EventEmitter<VideoPlayerComponentEvent>();
    @Output() videoLoaded = new EventEmitter<VideoPlayerComponentEvent>();
    @Output() seekedToPosition = new EventEmitter<PlaybackPositionChangedEvent>();
    @Output() videoEnded = new EventEmitter<void>();

    /** Start video for video question element because the user has clicked on "play" */
    @Output() showVideoForQuestionElement = new EventEmitter<number>();

    private readonly onDestroy$ = new Subject();

    private resetDynamicContentSubscription?: Subscription;

    /**
     * Current drawing-tool name. The fabric renderers consume it via the `activeDrawingTool`
     * getter (`[drawingTool]="activeDrawingTool"`); this component only stores the name.
     */
    private _drawingToolName: null | DrawingToolName = null;
    @Input() set drawingTool(drawingToolName: null | DrawingToolName) {
        this._drawingToolName = drawingToolName;
    }

    // --- Fabric canvas rendering ---
    // Image slides that are "fixed background + freehand drawing" render on the fabric
    // canvas: `picture` (foundation), plus `whiteboard`, `perceptionImage`
    // (canvas-image-slides), `bookpage` (canvas-bookpage-slides), `graphicboard`
    // (canvas-graphic-textboard-slides), `textboard`/`textboard_generated`
    // (canvas-textboard-slides) and `scrollVideo` (canvas-scroll-video-slides).
    // Every `canvas`-category slide renders on fabric (the legacy SVG canvas was removed).

    /** Whether the perception image for this slide has been revealed (synced Y.Doc display-state). */
    @Input() perceptionRevealed = false;

    /** Perception "show epoch" — changes on a same-slide content reset to replay the hide animation. */
    @Input() perceptionShowEpoch = 0;

    /** True for the perception-image slide (fixed background with an animated hide/reveal). */
    get isPerceptionImage(): boolean {
        return this.currentSlide?.type === SlideType.perceptionImage;
    }

    /** Numeric layer index for the fabric drawing-objects array, derived from `suffix`. */
    get fabricLayer(): 1 | 2 | 3 {
        return this.suffix === '-secondary' ? 2 : this.suffix === '-tertiary' ? 3 : 1;
    }

    /** Current drawing-tool name; the fabric renderers consume it via `[drawingTool]`. */
    get activeDrawingTool(): null | DrawingToolName {
        return this._drawingToolName;
    }

    /** Copyright credit of the current slide, shown on presentation screens for image slides only. */
    get copyrightNotice(): null | string {
        const copyright = this.currentSlide?.copyright?.trim();
        if (!copyright || !this.isPresentationScreen) return null;
        return isImageSlideType(this.currentSlide) ? copyright : null;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        // Rebuild the rubber input here (after all inputs are set for this change-detection
        // cycle) rather than inside `set commandStream$`. `currentSlide` and `commandStream$`
        // arrive via separate async streams (on the beamer, via Yjs sync), so building it in the
        // setter captured a stale/empty `currentSlide` and left the image URLs blank.
        if ('currentSlide' in changes || 'commandStream$' in changes) {
            this.buildRubberInput();
        }

        if ('resetDynamicContent$' in changes) {
            this.resetDynamicContentSubscription?.unsubscribe();
            this.resetDynamicContentSubscription = changes['resetDynamicContent$'].currentValue
                .pipe(
                    switchMap(() => this.resetDynamicContent()),
                    takeUntil(this.onDestroy$),
                )
                .subscribe();
        }

        // Pause/Resume videos when the lesson pause state changes
        if ('isLessonPaused' in changes && !changes['isLessonPaused'].isFirstChange()) {
            const isPaused = changes['isLessonPaused'].currentValue;

            // NOTE: commented out as on lesson resume, we don't know if the player _was_ playing or paused before pausing the lesson.
            //       Solution: only pause the player when the lesson gets paused. Do nothing when the lesson gets resumed
            //       --> player stays paused, the teacher can resume the video manually.
            if (isPaused && this.videoPlayerComponent) {
                await this.videoPlayerComponent.pauseVideo();
            }
            //isPaused === true ? this.videoPlayerComponent?.pauseVideo() : this.videoPlayerComponent?.playVideo();
        }

        if ('hidden' in changes && !changes['hidden'].isFirstChange()) {
            const isHidden = changes['hidden'].currentValue === true;

            // If the current slide is a video, pause it whenever the whole layer gets hidden
            // (one layer below becomes visible)
            if (isHidden && this.videoPlayerComponent) {
                await this.videoPlayerComponent.pauseVideo();
            }
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    /** Builds the rubber element input from the current slide's pictures and the rubber command stream. */
    private buildRubberInput(): void {
        const pictures = this.currentSlide?.rubberElementPictures;

        // A rubber slide with a missing image URL renders a blank element with no further error
        // (an empty `src` fires neither `onload` nor `onerror`). Surface it instead of failing silently.
        if (this.slideTypeCategory === 'rubber' && (!pictures?.foregroundImageUrl || !pictures?.backgroundImageUrl)) {
            this.log.warn('Rubber slide is missing image URL(s); element will render blank', {
                slideId: this.currentSlide?.id,
                hasForeground: !!pictures?.foregroundImageUrl,
                hasBackground: !!pictures?.backgroundImageUrl,
            });
        }

        this.rubberInput = {
            foregroundImageUrl: pictures?.foregroundImageUrl ?? '',
            backgroundImageUrl: pictures?.backgroundImageUrl ?? '',
            updates: this.updateRubberElementCommand,
        };
    }

    @ViewChild(VideoPlayerComponent)
    private videoPlayerComponent: VideoPlayerComponent;
    @ViewChild(RubberElementComponent)
    private rubberElementComponent: RubberElementComponent;
    @ViewChild(HtmlElementBoardComponent)
    private htmlElementBoardComponent: HtmlElementBoardComponent;
    @ViewChild(FabricSlideCanvasComponent)
    private fabricCanvas?: FabricSlideCanvasComponent;
    @ViewChild(PerceptionSlideComponent)
    private perceptionSlide?: PerceptionSlideComponent;

    /**
     * The export handle for the favorites flow: the directly rendered canvas, or the one nested
     * inside the perception component's own view (`canvas-slide-favorites` D7 — `@ViewChild`
     * does not descend into a child component's view).
     */
    private get exportCanvas(): FabricSlideCanvasComponent | undefined {
        return this.fabricCanvas ?? this.perceptionSlide?.fabricCanvas();
    }

    private async resetDynamicContent(): Promise<void> {
        switch (this.slideTypeCategory) {
            case 'video':
                await this.videoPlayerComponent?.restart();
                break;
            case 'rubber':
                this.rubberElementComponent.reset();
                break;
            case 'interactive': {
                this.htmlElementBoardComponent?.reset();
                break;
            }
        }
    }

    /**
     * Client-side image export for fabric drawing slides (`canvas-slide-favorites`): the 4K
     * master + all derived variants, generated entirely locally. A pristine slide (no
     * drawings/content, not a dynamically created type) returns null so the favorite
     * references the original slide image instead of uploading one. Throws on export/encode
     * failure — the save is aborted, never sent with a broken image.
     */
    private async exportFabricSlideImages(): Promise<FavoriteImageSet | null> {
        const canvas = this.exportCanvas;
        // A missing canvas is a failure, not a pristine slide — returning null here would
        // silently save the favorite WITHOUT its drawings (looks like success to the user).
        if (!canvas) throw new Error('Fabric slide export unavailable (canvas not attached)');
        const type = this.currentSlide?.type;
        // Panoramas always export, even without drawings: the save-time pan viewport is
        // meaningful state the favorite's thumbnails must capture. The favorite still opens
        // as a fully interactive panorama (the service resolves `panorama` from the original
        // slide; the full-width ladder becomes its image variants).
        const mustExport = (type !== undefined && isDynamicallyCreatedSlideType(type))
            || !!this.currentSlide?.panorama;
        if (!mustExport && !canvas.hasExportableContent()) return null;
        const exported = await canvas.exportToBlob();
        if (!exported) throw new Error('Fabric slide export unavailable (renderer not ready)');
        return buildFavoriteImageSet(exported);
    }

    async onAddToFavoritesClicked() {
        try {
            const evt = await this.createAddToFavoriteEventData();
            this.addToFavoritesClicked.next(evt);
        } catch (err) {
            // Spec "Export failure aborts the save": no dialog, no request with a broken image.
            this.log.error('Add-to-favorites export failed; save aborted', err as Error);
            this.snackBar.open('Beim Erstellen des Favoriten ist ein Fehler aufgetreten', undefined, {
                duration: 5000,
                panelClass: ['snackbar', 'error'],
            });
        }
    }

    async createAddToFavoriteEventData(): Promise<UserClickedOnAddToFavoritesButtonEvent> {
        const data: UserClickedOnAddToFavoritesButtonEvent = {
            payload: null,
            slide: this.currentSlide,
            slideTypeCategory: 'P',
        };

        const warn = () => this.log.warn(`handling of ${this.slideTypeCategory} slide type is not implemented yet.`);
        switch (this.slideTypeCategory) {
            case 'canvas': {
                data.slideTypeCategory = 'P';
                // Every 'canvas'-category slide renders on the fabric canvas and generates its
                // complete image set client-side (`canvas-slide-favorites`).
                const images = await this.exportFabricSlideImages();
                data.payload = images ? { kind: 'image', images } : null;
                break;
            }
            case 'video': {
                // Video favorites carry the Vimeo id; the favorite reopens the source video (the
                // 'canvas'-category is the only one that produces a rendered image set).
                const vimeoVideoId = data.slide.vimeoVideoId;
                data.payload = vimeoVideoId != null ? { kind: 'video', vimeoVideoId } : null;
                data.slideTypeCategory = 'V';
                break;
            }
            case 'interactive':
                data.slideTypeCategory = 'I';
                warn();
                break;
            case 'rubber':
                data.slideTypeCategory = 'R';
                warn();
                break;
            case 'difficult-question':
                data.slideTypeCategory = 'M';
                warn();
                break;
            case 'vdo':
                // Button is not rendered for VDO slides; this only guards the `default:` throw.
                warn();
                break;
            default:
                warn();
                throw new Error(`Cannot add a slide without type to favorites.`);
        }

        return data;
    }
}
